import React, { useState, useEffect } from "react"

import {
  get,
  child,
  update,
  getDatabase,
  ref as databaseRef,
} from "firebase/database"
import {
  query,
  where,
  getDocs,
  orderBy,
  collection,
  getFirestore,
} from "firebase/firestore"
import { format } from "date-fns"
import { getAuth } from "firebase/auth"
import { initializeApp } from "firebase/app"
import { ref, getStorage, uploadBytes, getDownloadURL } from "firebase/storage"

import { Helpers } from "@flexday/common-react"

import userIcon from "../assets/icons/user.svg"
import { Spinner } from "../assets/icons/spinner"
import Button from "../reusable-components/button"
import StandaloneInventoryCard from "../standalone-inventory-card"

const collectionOptions = {
  meetingRoom: "Meeting Room",
  flexdaySuite: "Flexday Suite",
  privateOffice: "Private Office",
}

const Profile = ({ profileUrl }) => {
  const { getFirebaseConfig } = Helpers
  const [user, setUser] = useState(null)
  const [photo, setPhoto] = useState(null)
  const [bookings, setBookings] = useState([])
  const [loadingBookings, setLoadingBookings] = useState(true)
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false)

  const firebaseConfig = getFirebaseConfig({
    REACT_APP_FIREBASE_MESSAGE_SENDER_ID:
      process.env.REACT_APP_FIREBASE_MESSAGE_SENDER_ID,
    REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
    REACT_APP_FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
    REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  })
  const app = initializeApp(firebaseConfig)
  const firestore = getFirestore(app)
  const database = getDatabase(app)
  const storage = getStorage(app)
  const auth = getAuth(app)

  auth.onAuthStateChanged((currentUser) => {
    if (user && !currentUser) {
      window.location.href = profileUrl
    }

    setUser(currentUser)
  })

  const getBookingsInfo = async () => {
    const bookingsList = []
    const currentTimestamp = new Date().getTime() - 24 * 60 * 60 * 1000

    const querySnapshot = await getDocs(
      query(
        collection(firestore, "conciergeBookings"),
        where("uid", "==", user.uid),
        where("startedAt", ">", currentTimestamp),
        orderBy("startedAt", "asc")
      )
    )

    if (!querySnapshot.empty) {
      querySnapshot.forEach((doc) => {
        const bookingData = doc.data()

        const dateRange = [
          ...new Set([
            format(new Date(bookingData.startedAt), "yyyy/M/d"),
            format(new Date(bookingData.endedAt), "yyyy/M/d"),
          ]),
        ].join(" - ")

        const filteredData = {
          dateRange,
          inventoryId: bookingData?.inventoryId,
          inventoryType: collectionOptions[bookingData?.inventoryType],
        }

        if (bookingData?.status === "active") bookingsList.push(filteredData)
      })

      setBookings(bookingsList)
    }

    setLoadingBookings(false)
  }

  const getUserPhoto = async () => {
    const snapshot = await get(
      child(databaseRef(database), `users/${user.uid}`)
    )

    if (snapshot.exists() && snapshot.val().photoUrl) {
      setPhoto(snapshot.val().photoUrl)
    }
  }

  useEffect(() => {
    if (user) {
      getUserPhoto()
      getBookingsInfo()
    }
  }, [user])

  const renderBookings = () => {
    if (!bookings.length) {
      return <p>You have no active reservations.</p>
    }

    return (
      <>
        <div
          className={`flex gap-12 w-screen pb-4 pl-4 scroll-smooth
                      overflow-auto md:gap-20 md:w-auto md:pl-8`}>
          {bookings.map((booking) => (
            <div
              className="inline-flex flex-col items-center gap-4 w-72 min-w-max"
              key={`${booking.inventoryId}-${booking.dateRange}`}>
              <p className="text-slate-500">{booking.dateRange}</p>
              {booking.inventoryId && booking.inventoryType && (
                <StandaloneInventoryCard
                  id={booking.inventoryId}
                  inventoryType={booking.inventoryType}
                />
              )}
            </div>
          ))}
        </div>
        <p className="px-2 italic text-slate-500">
          <b>Please note:</b> Flexday workspace amenities, photos and details
          may change at any point and differ from the information provided in
          this communication. Contact Flexday Concierge for the most updated
          information.
        </p>
      </>
    )
  }

  const handleUploadImage = async (e) => {
    try {
      setUploadingProfilePicture(true)
      const file = e.target.files[0]
      const storageRef = ref(
        storage,
        `users/${user.uid}/profile-photo/${new Date().toISOString()}`
      )

      await uploadBytes(storageRef, file)
      const photoUrl = await getDownloadURL(storageRef)
      await update(databaseRef(database, `users/${user.uid}`), { photoUrl })
      await getUserPhoto()
    } finally {
      setUploadingProfilePicture(false)
    }
  }

  const handleDeleteAccount = async () => {
    const isConfirmed = window.confirm(
      "Deleting your account will result in removing all of your information from our records, including your memberships and credits. This cannot be undone."
    )

    if (isConfirmed) {
      await auth.currentUser
        .delete()
        .then(() => {
          window.location.href = process.env.REACT_APP_FLEXDAY_URL
        })
        .catch(async (error) => {
          if (error.code === "auth/requires-recent-login") {
            window.alert(
              "Looks like you've logged in a while ago. We need to check it's really you before deleting the account. Please log out and back in again."
            )
          }
        })
    }
  }

  if (!user) {
    return null
  }

  return (
    <div
      className={`
        flex flex-col items-center gap-10 p-6
        sm:flex-row sm:items-stretch sm:gap-16
      `}>
      <div className="flex flex-col items-center gap-6 min-w-max">
        <img
          alt=""
          className="w-48 h-48 rounded-full"
          src={photo || auth?.currentUser?.photoURL || userIcon}
        />
        <input
          id="img"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUploadImage}
        />
        <label
          htmlFor="img"
          className="block cursor-pointer hover:underline text-xs text-slate-500 relative">
          Update Profile Picture
          {uploadingProfilePicture && (
            <Spinner className="absolute -top-2 -right-12" />
          )}
        </label>
      </div>

      <div className="flex flex-col gap-4 items-center sm:items-stretch overflow-hidden">
        <p className="text-4xl font-extrabold">{user?.displayName}</p>
        <a className="underline" href={`mailto:${user.email}`}>
          {user.email}
        </a>
        <p className="font-bold mt-2">Reservations</p>
        {loadingBookings ? <Spinner /> : renderBookings()}
        <Button
          title="Delete Account"
          onClick={handleDeleteAccount}
          className={`mt-16 max-w-max !text-red-700 font-semibold rounded-md bg-red-100 hover:bg-red-200`}
        />
      </div>
    </div>
  )
}

export default Profile
